import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import { MarkerCharts } from '../../DashboardCrypto/DashboardCryptoCharts';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ReportMarketGraph = ({ symbol = 'BTC', timeframe = '1d', from, to, predictions = [] }) => {
    const [chartData, setChartData] = useState([{ data: [] }]); // Initialize with proper structure
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPrice, setCurrentPrice] = useState(null);
    const [priceChange, setPriceChange] = useState(null);
    const [priceChangePercent, setPriceChangePercent] = useState(null);

    // Debug: Log predictions data
    useEffect(() => {
        console.log('ReportMarketGraph received predictions:', predictions);
        if (predictions.length > 0) {
            console.log('First prediction sample:', predictions[0]);
        }
    }, [predictions]);

    // Chart logic:
    // - For 4h, 1d, 2d, 7d predictions: Always show 1h chart data from last 7 days
    // - For other timeframes: Use the prediction timeframe with auto-expanded date ranges

    // Map timeframe to display period buttons
    const getTimeframePeriods = (tf) => {
        switch (tf) {
            case '4h':
                return [
                    { label: '1D', value: '1d' },
                    { label: '3D', value: '3d' },
                    { label: '1W', value: '1w' },
                    { label: '1M', value: '1m' }
                ];
            case '1d':
                return [
                    { label: '1W', value: '1w' },
                    { label: '1M', value: '1m' },
                    { label: '3M', value: '3m' },
                    { label: '6M', value: '6m' },
                    { label: '1Y', value: '1y' }
                ];
            default:
                return [
                    { label: '1H', value: '1h' },
                    { label: '1D', value: '1d' },
                    { label: '1W', value: '1w' },
                    { label: '1M', value: '1m' },
                    { label: 'ALL', value: 'all' }
                ];
        }
    };

    const [activePeriod, setActivePeriod] = useState('all');
    const periods = getTimeframePeriods(timeframe);

    useEffect(() => {
        console.log('ReportMarketGraph props:', { symbol, timeframe, from, to }); // Debug log
        
        // Cancel any pending request
        if (window.currentChartRequest) {
            window.currentChartRequest.cancel('New request initiated');
        }
        
        fetchChartData();
        
        // Cleanup function
        return () => {
            if (window.currentChartRequest) {
                window.currentChartRequest.cancel('Component unmounting or props changed');
            }
        };
    }, [symbol, timeframe, from, to]);

    const fetchChartData = async () => {
        setLoading(true);
        setError('');
        
        try {
            // Calculate optimal date range to make prediction triangle occupy ~1/3 of chart width
            let chartTimeframe = timeframe;
            let effectiveFrom = from;
            let effectiveTo = to;
            
            // If we have predictions, calculate date range based on prediction timeline
            if (predictions && predictions.length > 0) {
                // Find the earliest create_date and latest target_date from all predictions
                const predictionDates = predictions.map(p => ({
                    create: new Date(p.create_date),
                    target: new Date(p.target_date)
                })).filter(p => !isNaN(p.create.getTime()) && !isNaN(p.target.getTime()));
                
                if (predictionDates.length > 0) {
                    const earliestCreate = new Date(Math.min(...predictionDates.map(p => p.create.getTime())));
                    const latestTarget = new Date(Math.max(...predictionDates.map(p => p.target.getTime())));
                    
                    // Calculate prediction timespan
                    const predictionSpan = latestTarget.getTime() - earliestCreate.getTime();
                    
                    // For short prediction spans (like 1 day), ensure minimum chart width
                    const minChartSpan = 3 * 24 * 60 * 60 * 1000; // 3 days minimum
                    const effectivePredictionSpan = Math.max(predictionSpan, minChartSpan / 3); // At least 1 day for prediction part
                    
                    // To make prediction occupy 1/3 of chart, we need 2/3 additional time
                    // Split this: 1/3 before start, 1/3 after end
                    const additionalTime = effectivePredictionSpan * 2; // 2x the prediction span
                    const beforeTime = additionalTime / 2; // 1/3 of total chart
                    const afterTime = additionalTime / 2; // 1/3 of total chart
                    
                    const chartStartDate = new Date(earliestCreate.getTime() - beforeTime);
                    const chartEndDate = new Date(latestTarget.getTime() + afterTime);
                    
                    effectiveFrom = chartStartDate.toISOString().split('T')[0];
                    effectiveTo = chartEndDate.toISOString().split('T')[0];
                    
                    // Adjust chart timeframe based on total chart span for optimal granularity
                    const totalChartSpan = chartEndDate.getTime() - chartStartDate.getTime();
                    const daySpan = totalChartSpan / (24 * 60 * 60 * 1000);
                    
                    // Use more granular timeframes for prediction visualization
                    if (daySpan <= 2) {
                        chartTimeframe = '1h';
                    } else if (daySpan <= 7) {
                        chartTimeframe = '4h';
                    } else if (daySpan <= 30) {
                        chartTimeframe = '1d';
                    } else {
                        chartTimeframe = '1w';
                    }
                    
                    console.log(`📊 Optimized for prediction triangle (1/3 width):`, {
                        originalPredictionSpan: `${(predictionSpan / (24 * 60 * 60 * 1000)).toFixed(1)} days`,
                        effectivePredictionSpan: `${(effectivePredictionSpan / (24 * 60 * 60 * 1000)).toFixed(1)} days`,
                        totalChartSpan: `${daySpan.toFixed(1)} days`,
                        chartTimeframe,
                        dateRange: `${effectiveFrom} to ${effectiveTo}`,
                        predictionRange: `${earliestCreate.toISOString()} to ${latestTarget.toISOString()}`,
                        predictionTimestamps: {
                            create: earliestCreate.getTime(),
                            target: latestTarget.getTime()
                        }
                    });
                }
            }
            
            // Fallback logic for when no predictions or prediction dates are invalid
            if (!effectiveFrom || !effectiveTo) {
                // For longer prediction timeframes, use 1h chart data for better visualization
                if (['4h', '1d', '2d', '7d'].includes(timeframe)) {
                    chartTimeframe = '1h';
                    
                    // Always show last 7 days of 1h data for these timeframes
                    const endDate = to ? new Date(to) : new Date();
                    const startDate = new Date(endDate);
                    startDate.setDate(startDate.getDate() - 7);
                    
                    effectiveFrom = startDate.toISOString().split('T')[0];
                    effectiveTo = endDate.toISOString().split('T')[0];
                    
                    console.log(`📊 Using fallback 1h chart data for ${timeframe} prediction: last 7 days (${effectiveFrom} to ${effectiveTo})`);
                } else if (from && to && from === to) {
                    // Single day request for other timeframes - expand based on original timeframe
                    const endDate = new Date(to);
                    const startDate = new Date(endDate);
                    
                    switch (timeframe) {
                        case '1h':
                            startDate.setDate(startDate.getDate() - 3); // 3 days for hourly
                            break;
                        default:
                            startDate.setDate(startDate.getDate() - 7); // default 1 week
                    }
                    
                    effectiveFrom = startDate.toISOString().split('T')[0];
                    effectiveTo = endDate.toISOString().split('T')[0];
                    
                    console.log(`📅 Auto-expanded date range for ${timeframe}: ${from} → ${effectiveFrom} to ${effectiveTo}`);
                }
            }
            
            const params = new URLSearchParams({
                timeframe: chartTimeframe
            });
            
            if (effectiveFrom) params.append('from', effectiveFrom);
            if (effectiveTo) params.append('to', effectiveTo);

            const url = `${API}/api/chart-data/${symbol}?${params.toString()}`;
            console.log('🔍 Fetching chart data from:', url); // Debug log
            console.log('📊 Request params:', { 
                symbol, 
                predictionTimeframe: timeframe, 
                chartTimeframe, 
                originalFrom: from, 
                originalTo: to, 
                effectiveFrom, 
                effectiveTo 
            }); // Debug log

            // Create cancellable request
            const source = axios.CancelToken.source();
            window.currentChartRequest = source;

            const response = await axios.get(url, {
                cancelToken: source.token
            });
            
            // Clear the request reference on success
            window.currentChartRequest = null;
            
            console.log('📈 Chart data response:', response); // Debug log
            console.log('🔍 Response symbol check:', response?.symbol, 'vs requested:', symbol); // Debug log
            console.log('Response type:', typeof response); // Debug log
            console.log('Is array:', Array.isArray(response)); // Debug log
            console.log('Has data property:', response && response.data ? 'yes' : 'no'); // Debug log
            
            // Handle the axios interceptor that automatically extracts response.data
            // The API returns {symbol, data, total} but interceptor gives us just the data array
            let rawData = null;
            let responseSymbol = null;
            
            if (Array.isArray(response)) {
                // The interceptor returned the data array directly
                rawData = response;
                console.log('⚠️ Interceptor returned data array directly, length:', rawData.length); // Debug log
            } else if (response && response.data && Array.isArray(response.data)) {
                // Full object response (if interceptor didn't trigger)
                rawData = response.data;
                responseSymbol = response.symbol;
                console.log('Using object.data format, length:', rawData.length); // Debug log
                
                // Validate symbol matches
                if (responseSymbol && responseSymbol.toUpperCase() !== symbol.toUpperCase()) {
                    console.error('❌ Symbol mismatch!', { 
                        requested: symbol.toUpperCase(), 
                        received: responseSymbol.toUpperCase() 
                    });
                    setError(`Data mismatch: requested ${symbol} but got ${responseSymbol}`);
                    setChartData([{ data: [] }]);
                    return;
                }
            } else {
                // Unexpected format
                console.error('❌ Unexpected response format:', response);
                setError('Unexpected response format from server');
                setChartData([{ data: [] }]);
                return;
            }
            
            if (rawData && rawData.length > 0) {
                console.log('📊 Raw data analysis:', {
                    totalPoints: rawData.length,
                    firstPoint: rawData[0],
                    lastPoint: rawData[rawData.length - 1],
                    dateRange: {
                        first: new Date(rawData[0].x),
                        last: new Date(rawData[rawData.length - 1].x)
                    }
                }); // Debug log
                
                // Check if we have insufficient data points for a meaningful chart
                // For 1h data (which we use for most charts), expect more data points
                const expectedHourlyPoints = 7 * 24; // 7 days * 24 hours
                const minPointsNeeded = chartTimeframe === '1h' ? Math.min(expectedHourlyPoints * 0.5, 50) : 10;
                
                if (rawData.length < minPointsNeeded) {
                    console.warn(`⚠️ Lower than expected data points (${rawData.length} < ${minPointsNeeded}) for ${chartTimeframe} chart.`);
                    // Still proceed with the data we have
                }
                
                // Ensure data format is correct for ApexCharts - matches MarketGraphAll format
                const formattedData = rawData.map(item => ({
                    x: new Date(item.x), // Use Date object, not timestamp
                    y: item.y // [open, high, low, close]
                }));
                
                console.log('Formatted chart data sample:', formattedData.slice(0, 3)); // Debug log
                console.log('Chart series structure:', [{ data: formattedData }]); // Debug log
                
                setChartData([{ data: formattedData }]);
                
                // Calculate price change if we have data
                const data = formattedData;
                if (data.length > 1) {
                    const latest = data[data.length - 1];
                    const previous = data[data.length - 2];
                    const current = latest.y[3]; // close price
                    const prev = previous.y[3]; // previous close price
                    const change = current - prev;
                    const changePercent = ((change / prev) * 100);
                    
                    setCurrentPrice(current);
                    setPriceChange(change);
                    setPriceChangePercent(changePercent);
                }
            } else {
                console.warn('No chart data received'); // Debug log
                console.log('Raw data is null or empty:', rawData); // Debug log
                setChartData([{ data: [] }]);
            }
        } catch (err) {
            // Clear the request reference on error
            window.currentChartRequest = null;
            
            if (axios.isCancel(err)) {
                console.log('🚫 Chart data request cancelled:', err.message);
                return; // Don't update state for cancelled requests
            }
            
            console.error('Error fetching chart data:', err);
            setError('Failed to load chart data');
            setChartData([{ data: [] }]);
        } finally {
            setLoading(false);
        }
    };

    const handlePeriodChange = (period) => {
        setActivePeriod(period);
        // For now, just update the active period
        // In a full implementation, this would adjust the date range
    };

    return (
        <React.Fragment>
            <Row>
                <Col xl={12}>
                    <Card>
                        <CardHeader className="border-0 align-items-center d-flex">
                            <h4 className="card-title mb-0 flex-grow-1">
                                {symbol} Market Chart ({timeframe})
                            </h4>
                            <div className="d-flex gap-1">
                                {periods.map((period) => (
                                    <button 
                                        key={period.value}
                                        type="button" 
                                        className={`btn btn-soft-${activePeriod === period.value ? 'primary' : 'secondary'} btn-sm`} 
                                        onClick={() => handlePeriodChange(period.value)}
                                    >
                                        {period.label}
                                    </button>
                                ))}
                            </div>
                        </CardHeader>
                        <CardBody className="p-0">
                            <div className="bg-light-subtle border-top-dashed border border-start-0 border-end-0 border-bottom-dashed py-3 px-4">
                                <Row className="align-items-center">
                                    <Col xs={6}>
                                        <div className="d-flex flex-wrap gap-4 align-items-center">
                                            {currentPrice && (
                                                <>
                                                    <h5 className="fs-19 mb-0">{currentPrice.toFixed(2)}</h5>
                                                    {priceChange !== null && priceChangePercent !== null && (
                                                        <p className="fw-medium text-muted mb-0">
                                                            ${Math.abs(priceChange).toFixed(2)} 
                                                            <span className={`fs-11 ms-1 ${priceChange >= 0 ? 'text-success' : 'text-danger'}`}>
                                                                {priceChange >= 0 ? '+' : ''}{priceChangePercent.toFixed(2)}%
                                                            </span>
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                            {loading && <p className="fw-medium text-muted mb-0">Loading...</p>}
                                            {error && <p className="fw-medium text-danger mb-0">{error}</p>}
                                        </div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="d-flex">
                                            <div className="d-flex justify-content-end text-end flex-wrap gap-4 ms-auto">
                                                <div className="pe-3">
                                                    <h6 className="mb-2 text-truncate text-muted">Symbol</h6>
                                                    <h5 className="mb-0">{symbol}</h5>
                                                </div>
                                                <div className="pe-3">
                                                    <h6 className="mb-2 text-muted">Timeframe</h6>
                                                    <h5 className="mb-0">{timeframe}</h5>
                                                </div>
                                                <div className="pe-3">
                                                    <h6 className="mb-2 text-muted">Data Points</h6>
                                                    <h5 className="mb-0">{chartData[0]?.data?.length || 0}</h5>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </CardBody>
                        <div className="card-body p-0 pb-3">
                            <div id="Market_chart" className="apex-charts" dir="ltr">
                                {chartData[0]?.data?.length > 0 ? (
                                    <MarkerCharts 
                                        series={chartData} 
                                        dataColors='["--vz-success", "--vz-danger"]'
                                        predictions={predictions}
                                    />
                                ) : (
                                    <div style={{ height: '294px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {loading ? (
                                            <p className="text-muted">Loading chart data...</p>
                                        ) : error ? (
                                            <p className="text-danger">{error}</p>
                                        ) : (
                                            <p className="text-muted">No chart data available</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default ReportMarketGraph;
